import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface ChannelInvitationAcceptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notificationId: string;
  invitationId: string;
  channelId: string;
  channelName: string;
  inviterName: string;
}

export const ChannelInvitationAcceptDialog = ({
  open,
  onOpenChange,
  notificationId,
  invitationId,
  channelId,
  channelName,
  inviterName,
}: ChannelInvitationAcceptDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const handleAccept = async () => {
    setIsProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get channel slug for navigation
      const { data: channel } = await supabase
        .from('channels')
        .select('slug')
        .eq('id', channelId)
        .single();

      // Add user to user_organizations for this channel
      const { error: orgError } = await supabase
        .from('user_organizations')
        .insert({
          user_id: user.id,
          organization_name: channelName,
          channel_id: channelId
        });

      // Ignore duplicate error (user already in channel)
      if (orgError && !orgError.message.includes('duplicate')) {
        throw orgError;
      }

      // Update invitation status to 'accepted'
      const { error: invitationError } = await supabase
        .from('invitations')
        .update({ 
          invitation_status: 'accepted',
          accepted_at: new Date().toISOString(),
          registered_user_id: user.id
        })
        .eq('id', invitationId);

      if (invitationError) throw invitationError;

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      // Notify the inviter
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();

      const { data: invitation } = await supabase
        .from('invitations')
        .select('inviter_id')
        .eq('id', invitationId)
        .single();

      if (invitation?.inviter_id) {
        await supabase.from('notifications').insert({
          user_id: invitation.inviter_id,
          type: 'channel_invitation_accepted',
          title: 'Channel Invitation Accepted',
          message: `${profile?.display_name || 'A user'} accepted your invitation to join ${channelName}!`,
          metadata: {
            channel_id: channelId,
            channel_name: channelName,
            accepter_name: profile?.display_name
          }
        });
      }

      toast({
        title: "Channel joined!",
        description: `You've successfully joined the ${channelName} channel.`,
      });

      onOpenChange(false);
      
      // Navigate to the channel page
      const channelSlug = channel?.slug || channelName.toLowerCase().replace(/\s+/g, '-');
      navigate(`/${channelSlug}-channel`);

    } catch (error) {
      console.error('Error accepting channel invitation:', error);
      toast({
        title: "Failed to join channel",
        description: "There was an error joining the channel. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!showDeclineForm) {
      setShowDeclineForm(true);
      return;
    }

    setIsProcessing(true);
    
    try {
      // Update invitation status to 'declined'
      const { error: invitationError } = await supabase
        .from('invitations')
        .update({ 
          invitation_status: 'declined'
        })
        .eq('id', invitationId);

      if (invitationError) throw invitationError;

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      // Send notification to inviter about the decline
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user?.id)
        .single();
      
      const { data: invitation } = await supabase
        .from('invitations')
        .select('inviter_id')
        .eq('id', invitationId)
        .single();

      if (invitation?.inviter_id) {
        await supabase.from('notifications').insert({
          user_id: invitation.inviter_id,
          type: 'channel_invitation_declined',
          title: 'Channel Invitation Declined',
          message: `${profile?.display_name || 'A user'} declined your invitation to join ${channelName}${declineReason ? `: "${declineReason}"` : '.'}`,
          metadata: {
            channel_id: channelId,
            channel_name: channelName,
            decliner_name: profile?.display_name,
            decline_reason: declineReason
          }
        });
      }

      toast({
        title: "Invitation declined",
        description: "You've declined the channel invitation.",
      });

      onOpenChange(false);

    } catch (error) {
      console.error('Error declining channel invitation:', error);
      toast({
        title: "Failed to decline invitation",
        description: "There was an error processing your response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (showDeclineForm) {
      setShowDeclineForm(false);
      setDeclineReason("");
    } else {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Channel Invitation
          </DialogTitle>
          <DialogDescription>
            You've been invited to join a channel
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">From:</p>
            <p className="text-base font-semibold">{inviterName}</p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Channel:</p>
            <p className="text-base font-semibold text-primary">{channelName}</p>
          </div>

          {showDeclineForm ? (
            <div className="space-y-2">
              <Label htmlFor="decline-reason">Reason for declining (optional)</Label>
              <Textarea
                id="decline-reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Let the inviter know why you're declining..."
                rows={3}
              />
            </div>
          ) : (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground">
                Would you like to join the {channelName} channel and access its knowledge and collaboration features?
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {showDeclineForm ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Confirm Decline
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={isProcessing}
              >
                <XCircle className="mr-2 h-4 w-4" />
                No, Decline
              </Button>
              <Button
                onClick={handleAccept}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Yes, Join Channel
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
